import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, SpinnerGap } from '@phosphor-icons/react';

export default function LabelVerify() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState({ status: 'loading' });
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const token = params.get('token');
    if (!token) { setState({ status: 'error', message: 'Missing verification token' }); return; }
    api.post('/labels/claim/verify', { token })
      .then(r => setState({ status: 'ok', label: r.data.label, email: r.data.work_email }))
      .catch(e => setState({ status: 'error', message: e.response?.data?.detail || 'Verification failed' }));
  }, [params]);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
      <div data-testid="label-verify-card" className="w-full max-w-md border border-zinc-800 rounded-2xl bg-zinc-900/50 p-8 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">TuneAmplifier · Label access</div>

        {state.status === 'loading' && (
          <div className="mt-6" data-testid="verify-loading">
            <SpinnerGap size={34} className="text-[#ccff00] mx-auto animate-spin" />
            <p className="mt-4 text-sm text-zinc-400 font-mono">Verifying your work email…</p>
          </div>
        )}

        {state.status === 'ok' && (
          <div className="mt-6" data-testid="verify-success">
            <CheckCircle size={40} weight="fill" className="text-[#ccff00] mx-auto" />
            <h1 className="font-display text-3xl font-bold mt-4">You're verified.</h1>
            <p className="text-sm text-zinc-400 mt-2">
              <span className="text-zinc-100 font-medium">{state.label?.name}</span> is now yours to manage
              {state.email ? <> as <span className="font-mono text-[#ccff00]">{state.email}</span></> : null}.
              Incoming demos land in your inbox.
            </p>
            <Button data-testid="verify-goto-portal" onClick={() => nav('/label-portal')} className="btn-neon rounded-full mt-6 h-11 px-6 font-display font-semibold">
              Open demo inbox
            </Button>
          </div>
        )}

        {state.status === 'error' && (
          <div className="mt-6" data-testid="verify-error">
            <XCircle size={40} weight="fill" className="text-red-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-4">Link didn't work</h1>
            <p className="text-sm text-zinc-400 mt-2">{state.message}</p>
            <Button data-testid="verify-retry-btn" onClick={() => nav('/label-portal')} className="rounded-full mt-6 h-11 px-6 border border-zinc-800 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:border-[#ccff00]/60 font-display">
              Back to Label Portal
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
